import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import LocaleTabs from '../components/LocaleTabs';
import {
  makeEmptyTranslations,
  fetchCourseTranslations,
  saveCourseTranslations,
  localeDir,
} from '../utils/courseTranslations';
import './CreateCourse.css';

const CreateCourse = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin' || user?.role === 'sub-admin';
  const isInstructor = user?.role === 'instructor';
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    thumbnail_url: '',
    price: '20',
    is_free: false,
    requires_subscription: false,
  });
  const [videos, setVideos] = useState([
    { title: '', description: '', youtube_url: '', duration: '' },
  ]);
  const [createLocale, setCreateLocale] = useState('en');
  const [createTranslations, setCreateTranslations] = useState(makeEmptyTranslations);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: '',
    title: '',
    description: '',
    category_id: '',
    thumbnail_url: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editPrefillLoading, setEditPrefillLoading] = useState(false);
  const [editLocale, setEditLocale] = useState('en');
  const [editTranslations, setEditTranslations] = useState(makeEmptyTranslations);
  const [editLoadedLocales, setEditLoadedLocales] = useState([]);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');

  // Create course modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      setCoursesLoading(true);
      try {
        if (isAdmin) {
          const response = await api.get('/admin/courses?limit=50');
          setCourses(response.data.courses || []);
          setSelectedCourse((prev) => prev || response.data.courses?.[0] || null);
          const instructorRes = await api.get('/admin/users?role=instructor&limit=100');
          setInstructors(instructorRes.data.users || []);
        } else if (isInstructor) {
          // Use the specific instructor endpoint which only returns their courses
          const response = await api.get('/analytics/instructor');
          const myCourses = response.data.course_performance || [];
          setCourses(myCourses);
          setSelectedCourse((prev) => prev || myCourses[0] || null);
        } else {
          setCourses([]);
          setSelectedCourse(null);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError(err.response?.data?.error || 'Failed to load courses. Please refresh.');
      } finally {
        setCoursesLoading(false);
      }
    };

    fetchCourses();
  }, [isAdmin, isInstructor]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleCreateTranslationChange = (field, value) => {
    setCreateTranslations((prev) => ({
      ...prev,
      [createLocale]: { ...prev[createLocale], [field]: value },
    }));
  };

  const handleEditTranslationChange = (field, value) => {
    setEditTranslations((prev) => ({
      ...prev,
      [editLocale]: { ...prev[editLocale], [field]: value },
    }));
  };

  const handleVideoChange = (index, field, value) => {
    const newVideos = [...videos];
    newVideos[index][field] = value;

    if (field === 'youtube_url' && value) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = value.match(regExp);
      if (match && match[2].length === 11) {
        newVideos[index].youtube_id = match[2];
      }
    }

    setVideos(newVideos);
  };

  const addVideoField = () => {
    setVideos([...videos, { title: '', description: '', youtube_url: '', duration: '' }]);
  };

  const removeVideoField = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handlePublishToggle = async (courseId) => {
    try {
      await api.put(`/admin/courses/${courseId}/publish`);
      const refreshed = await api.get('/admin/courses?limit=50');
      setCourses(refreshed.data.courses || []);
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(
          refreshed.data.courses.find((course) => course.id === courseId) || null
        );
      }
    } catch (err) {
      console.error('Error updating course status:', err);
      alert('Unable to update course status.');
    }
  };

  // Open edit modal with course data
  const handleEditClick = async (course) => {
    setEditFormData({
      id: course.id,
      title: course.title || '',
      description: course.description || '',
      category_id: course.category_id || '',
      thumbnail_url: course.thumbnail_url || '',
    });
    setEditLocale('en');
    setEditTranslations(makeEmptyTranslations());
    setEditLoadedLocales([]);
    setEditPrefillLoading(true);
    setShowEditModal(true);

    try {
      // Re-fetch base fields in English — list data may be localized, and
      // saving a localized title back would overwrite the English original.
      const [baseRes, { translations, loadedLocales }] = await Promise.all([
        api.get(`/courses/${course.id}`, { params: { lang: 'en' } }),
        fetchCourseTranslations(course.id),
      ]);
      setEditFormData((prev) => ({
        ...prev,
        title: baseRes.data.title || '',
        description: baseRes.data.description || '',
      }));
      setEditTranslations(translations);
      setEditLoadedLocales(loadedLocales);
    } catch (err) {
      console.error('Error loading course translations:', err);
    } finally {
      setEditPrefillLoading(false);
    }
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  // Submit edit form
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.title.trim() || !editFormData.description.trim()) {
      setEditLocale('en');
      alert(t('editCourse.englishContentRequired'));
      return;
    }
    setEditLoading(true);
    try {
      await api.put(`/courses/${editFormData.id}`, {
        title: editFormData.title,
        description: editFormData.description,
        category_id: editFormData.category_id,
        thumbnail_url: editFormData.thumbnail_url,
      });
      await saveCourseTranslations(editFormData.id, editTranslations, editLoadedLocales);
      // Refresh courses list
      const refreshed = await api.get('/admin/courses?limit=50');
      setCourses(refreshed.data.courses || []);
      if (selectedCourse?.id === editFormData.id) {
        setSelectedCourse(
          refreshed.data.courses.find((course) => course.id === editFormData.id) || null
        );
      }
      setShowEditModal(false);
      alert('Course updated successfully!');
    } catch (err) {
      console.error('Error updating course:', err);
      alert(err.response?.data?.error || 'Failed to update course');
    } finally {
      setEditLoading(false);
    }
  };

  // Open delete confirmation
  const handleDeleteClick = (course) => {
    setCourseToDelete(course);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/courses/${courseToDelete.id}`);
      // Refresh courses list
      const refreshed = await api.get('/admin/courses?limit=50');
      setCourses(refreshed.data.courses || []);
      if (selectedCourse?.id === courseToDelete.id) {
        setSelectedCourse(refreshed.data.courses?.[0] || null);
      }
      setShowDeleteConfirm(false);
      setCourseToDelete(null);
      alert('Course deleted successfully!');
    } catch (err) {
      console.error('Error deleting course:', err);
      alert(err.response?.data?.error || 'Failed to delete course');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim() || !formData.description.trim()) {
      setCreateLocale('en');
      setError(t('editCourse.englishContentRequired'));
      return;
    }

    setLoading(true);

    try {
      const courseResponse = await api.post('/courses', formData);
      const courseId = courseResponse.data.id;

      try {
        await saveCourseTranslations(courseId, createTranslations);
      } catch (translationErr) {
        // Course already created — don't fail the whole flow; translations
        // can be re-saved from the editor.
        console.error('Error saving course translations:', translationErr);
      }

      for (let i = 0; i < videos.length; i += 1) {
        const video = videos[i];
        if (video.title && video.youtube_url) {
          const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
          const match = video.youtube_url.match(regExp);
          const youtubeId = match && match[2].length === 11 ? match[2] : video.youtube_url;

          await api.post('/videos', {
            course_id: courseId,
            title: video.title,
            description: video.description,
            youtube_url: video.youtube_url,
            youtube_id: youtubeId,
            duration: video.duration ? parseInt(video.duration, 10) : null,
            order_index: i,
          });
        }
      }

      if (isAdmin) {
        navigate(`/edit-course/${courseId}`, { state: { from: '/admin', tab: 'courses' } });
      } else {
        navigate(`/edit-course/${courseId}`);
      }
    } catch (err) {
      console.error('Error creating course:', err);
      setError(err.response?.data?.error || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = (courseId) => {
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  const filteredCourses = courses.filter((course) => {
    const term = searchTerm.toLowerCase();
    return (
      course.title?.toLowerCase().includes(term) ||
      course.category_name?.toLowerCase().includes(term) ||
      course.instructor_name?.toLowerCase().includes(term)
    );
  });

  const renderCourseAccordion = () => (
    <div className="course-accordion-container">
      {coursesLoading ? (
        <p className="no-data">{t('editCourse.loadingCourses')}</p>
      ) : filteredCourses.length === 0 ? (
        <p className="no-data">{t('editCourse.noCoursesFoundSearch')}</p>
      ) : (
        <div className="accordion-list">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className={`accordion-item ${expandedCourseId === course.id ? 'expanded' : ''}`}
            >
              <div className="accordion-header" onClick={() => toggleAccordion(course.id)}>
                <div className="header-info">
                  <div className="title-group">
                    <span className="course-title">{course.title}</span>
                    <span className="course-category">
                      {course.category_name || t('editCourse.generalCategory')}
                    </span>
                  </div>
                  <div className="meta-group">
                    {isAdmin && (
                      <span className="instructor-badge">
                        {course.instructor_name || '—'}
                      </span>
                    )}
                    <span className={`status-pill ${course.is_published ? 'active' : 'inactive'}`}>
                      {course.is_published ? t('editCourse.statusActive') : t('editCourse.statusInactive')}
                    </span>
                    <span className="learner-count">
                      {course.enrollment_count ?? course.enrollments ?? 0} {t('editCourse.learnersLabel')}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    type="button"
                    className="btn-icon-toggle"
                    aria-label="Toggle Details"
                  >
                    {expandedCourseId === course.id ? '−' : '+'}
                  </button>
                </div>
              </div>

              {expandedCourseId === course.id && (
                <div className="accordion-content">
                  <div className="content-grid">
                    <div className="description-section">
                      <h5>{t('editCourse.accordionDescriptionHeading')}</h5>
                      <p>{course.description || t('editCourse.noDescriptionAvailable')}</p>
                    </div>
                    <div className="stats-section">
                      <div className="stat-item">
                        <span className="label">{t('editCourse.categoryStatLabel')}</span>
                        <strong>{course.category_name || t('editCourse.generalCategory')}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">{t('editCourse.createdStatLabel')}</span>
                        <strong>
                          {course.created_at
                            ? new Date(course.created_at).toLocaleDateString()
                            : '—'}
                        </strong>
                      </div>
                      {isAdmin && (
                        <div className="stat-item">
                          <span className="label">{t('editCourse.instructorStatLabel')}</span>
                          <strong>{course.instructor_name || '—'}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="content-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      {t('editCourse.viewPageBtn')}
                    </button>
                    <button
                      type="button"
                      className="btn-edit"
                      onClick={() => handleEditClick(course)}
                    >
                      {t('editCourse.quickEditBtn')}
                    </button>
                    <button
                      type="button"
                      className="btn-edit-full"
                      onClick={() => navigate(`/edit-course/${course.id}`)}
                    >
                      {t('editCourse.fullEditorBtn')}
                    </button>
                    <button
                      type="button"
                      className={course.is_published ? 'btn-warning' : 'btn-success'}
                      onClick={() => handlePublishToggle(course.id)}
                    >
                      {course.is_published ? t('editCourse.unpublishBtn') : t('editCourse.publishBtn')}
                    </button>
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => handleDeleteClick(course)}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const lessonCount = videos.filter(
    (video) => video.title?.trim() || video.youtube_url?.trim()
  ).length;

  return (
    <div className="page manage-courses-page">
      <div className="container">
        <section className="manage-hero">
          <div className="hero-content">
            <p className="eyebrow">{t('editCourse.courseWorkspaceEyebrow')}</p>
            <h1>{isAdmin ? t('editCourse.manageCoursesHeading') : t('editCourse.createNewCourseHeading')}</h1>
            <p>
              {isAdmin
                ? t('editCourse.manageCoursesDesc')
                : t('editCourse.createNewCourseDesc')}
            </p>
            <button
              type="button"
              className="btn-primary btn-hero-cta"
              onClick={() => setShowCreateModal(true)}
            >
              {t('editCourse.createNewCourseBtn')}
            </button>
            {isAdmin && (
              <button
                type="button"
                className="btn-secondary"
                style={{ marginTop: '10px' }}
                onClick={() => navigate('/admin', { state: { activeTab: 'courses' } })}
              >
                {t('editCourse.backToAdminBtn')}
              </button>
            )}
          </div>
          <div className="hero-card">
            <span className="label">{t('editCourse.lessonsInDraft')}</span>
            <p className="hero-value">{lessonCount || 0}</p>
            <p className="hero-note">{t('editCourse.addLessonsAsYouGo')}</p>
          </div>
        </section>

        <div className="course-management-section">
          <div className="section-header-row">
            <div className="panel-header-simple">
              <p className="label">{t('editCourse.managementLabel')}</p>
              <h2>{isAdmin ? t('editCourse.allCoursesHeading') : t('editCourse.yourCoursesHeading')}</h2>
            </div>
            <div className="search-bar-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('editCourse.searchCoursesPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {renderCourseAccordion()}
        </div>

        {/* Create Course Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('editCourse.courseBuilderTitle')}</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setShowCreateModal(false)}
                >
                  &times;
                </button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="form-section">
                    <h3>{t('editCourse.courseInformationSection')}</h3>

                    {isAdmin && (
                      <div className="form-group">
                        <label>{t('editCourse.instructorOptionalLabel')}</label>
                        <select
                          name="instructor_id"
                          value={formData.instructor_id || ''}
                          onChange={handleChange}
                        >
                          <option value="">{t('editCourse.selectInstructorDefault')}</option>
                          {instructors.map((instr) => (
                            <option key={instr.id} value={instr.id}>
                              {instr.first_name} {instr.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="form-group">
                      <label>{t('editCourse.contentLanguageLabel')}</label>
                      <LocaleTabs
                        active={createLocale}
                        onChange={setCreateLocale}
                        translations={createTranslations}
                      />
                      {createLocale !== 'en' && (
                        <p className="locale-hint">{t('editCourse.translationFallbackHint')}</p>
                      )}
                    </div>

                    {createLocale === 'en' ? (
                      <>
                        <div className="form-group">
                          <label>{t('editCourse.courseTitleRequired')}</label>
                          <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>{t('editCourse.descriptionRequired')}</label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>{t('editCourse.translatedTitleLabel')}</label>
                          <input
                            type="text"
                            dir={localeDir(createLocale)}
                            value={createTranslations[createLocale].title}
                            onChange={(e) => handleCreateTranslationChange('title', e.target.value)}
                          />
                        </div>

                        <div className="form-group">
                          <label>{t('editCourse.translatedDescriptionLabel')}</label>
                          <textarea
                            dir={localeDir(createLocale)}
                            value={createTranslations[createLocale].description}
                            onChange={(e) => handleCreateTranslationChange('description', e.target.value)}
                            rows="4"
                          />
                        </div>
                      </>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('editCourse.categoryRequired')}</label>
                        <select
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">{t('editCourse.selectCategory')}</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>{t('editCourse.thumbnailUrlOptional')}</label>
                        <input
                          type="url"
                          name="thumbnail_url"
                          value={formData.thumbnail_url}
                          onChange={handleChange}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('editCourse.priceDollarLabel')}</label>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          placeholder="20.00"
                        />
                      </div>
                    </div>
                    <div className="form-group checkbox-group" style={{ marginTop: 12 }}>
                      <label>
                        <input
                          type="checkbox"
                          name="requires_subscription"
                          checked={formData.requires_subscription}
                          onChange={handleChange}
                        />
                        {' '}{t('editCourse.requiresSubscriptionLabel')}
                      </label>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>{t('editCourse.courseVideosSection')}</h3>

                    {videos.map((video, index) => (
                      <div key={index} className="video-form-group">
                        <div className="video-form-header">
                          <h4>{t('editCourse.videoLabel', { n: index + 1 })}</h4>
                          {videos.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeVideoField(index)}
                              className="btn-danger btn-small"
                            >
                              {t('editCourse.removeBtn')}
                            </button>
                          )}
                        </div>

                        <div className="form-group">
                          <label>{t('editCourse.videoTitleInputLabel')}</label>
                          <input
                            type="text"
                            value={video.title}
                            onChange={(e) =>
                              handleVideoChange(index, 'title', e.target.value)
                            }
                            placeholder={t('editCourse.videoTitlePlaceholder')}
                          />
                        </div>

                        <div className="form-group">
                          <label>{t('editCourse.videoDescInputLabel')}</label>
                          <textarea
                            value={video.description}
                            onChange={(e) =>
                              handleVideoChange(index, 'description', e.target.value)
                            }
                            rows="2"
                            placeholder={t('editCourse.videoDescPlaceholder')}
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('editCourse.youtubeUrlInputLabel')}</label>
                            <input
                              type="url"
                              value={video.youtube_url}
                              onChange={(e) =>
                                handleVideoChange(index, 'youtube_url', e.target.value)
                              }
                              placeholder={t('editCourse.youtubeUrlPlaceholder')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('editCourse.durationInputLabel')}</label>
                            <input
                              type="number"
                              value={video.duration}
                              onChange={(e) =>
                                handleVideoChange(index, 'duration', e.target.value)
                              }
                              placeholder={t('editCourse.durationPlaceholder')}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={addVideoField}
                    >
                      {t('editCourse.addAnotherVideoBtn')}
                    </button>
                  </div>

                  {error && <p className="error-message">{error}</p>}

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowCreateModal(false)}
                    >
                      {t('editCourse.buttonCancel')}
                    </button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? t('editCourse.buttonSavingChanges') : t('editCourse.saveCourseBtn')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Course Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('editCourse.editCourseModalTitle')}</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setShowEditModal(false)}
                >
                  &times;
                </button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="form-group">
                  <label>{t('editCourse.contentLanguageLabel')}</label>
                  <LocaleTabs
                    active={editLocale}
                    onChange={setEditLocale}
                    translations={editTranslations}
                  />
                  {editLocale !== 'en' && (
                    <p className="locale-hint">{t('editCourse.translationFallbackHint')}</p>
                  )}
                </div>

                {editLocale === 'en' ? (
                  <>
                    <div className="form-group">
                      <label>{t('editCourse.courseTitleLabel')}</label>
                      <input
                        type="text"
                        name="title"
                        value={editFormData.title}
                        onChange={handleEditChange}
                        disabled={editPrefillLoading}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('editCourse.descriptionLabel')}</label>
                      <textarea
                        name="description"
                        value={editFormData.description}
                        onChange={handleEditChange}
                        rows="4"
                        disabled={editPrefillLoading}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>{t('editCourse.translatedTitleLabel')}</label>
                      <input
                        type="text"
                        dir={localeDir(editLocale)}
                        value={editTranslations[editLocale].title}
                        onChange={(e) => handleEditTranslationChange('title', e.target.value)}
                        disabled={editPrefillLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('editCourse.translatedDescriptionLabel')}</label>
                      <textarea
                        dir={localeDir(editLocale)}
                        value={editTranslations[editLocale].description}
                        onChange={(e) => handleEditTranslationChange('description', e.target.value)}
                        rows="4"
                        disabled={editPrefillLoading}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>{t('editCourse.categoryLabel')}</label>
                  <select
                    name="category_id"
                    value={editFormData.category_id}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">{t('editCourse.selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('editCourse.thumbnailUrlLabel')}</label>
                  <input
                    type="url"
                    name="thumbnail_url"
                    value={editFormData.thumbnail_url}
                    onChange={handleEditChange}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowEditModal(false)}
                  >
                    {t('editCourse.buttonCancel')}
                  </button>
                  <button type="submit" className="btn-primary" disabled={editLoading}>
                    {editLoading ? t('editCourse.buttonSavingChanges') : t('editCourse.buttonSaveChanges')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && courseToDelete && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('editCourse.deleteCourseTitle')}</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="warning-icon">⚠️</div>
                <p>{t('editCourse.deleteWarningQuestion')}</p>
                <p className="course-name-confirm">"{courseToDelete.title}"</p>
                <p className="warning-text">
                  {t('editCourse.deleteWarningText')}
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  {t('editCourse.buttonCancel')}
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? t('editCourse.deletingBtn') : t('editCourse.deleteCourseBtn')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCourse;
